import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletWriteCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type { WalletDto } from "@/features/wallet/api/dto";

/**
 * Crée un portefeuille (`POST /Wallets/add`) — étape 3 du parcours
 * d'inscription. Il naît en PENDING_KYC / TIER0 : aucune transaction
 * possible avant l'approbation KYC (étape 6).
 */
export const useCreateWallet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "wallets", "create"],
    mutationFn: (body: WalletDto) =>
      mutateData({
        url: walletEndpoints.wallets.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<WalletDto>,
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, walletWriteCacheKeys(), {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateWallet failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
